import React, { lazy } from 'react';
import ReactDOM from 'react-dom';
import registerServiceWorker from './registerServiceWorker';
import { createBrowserHistory } from "history";
import { Router, Switch, Route, Redirect } from 'react-router-dom'
import { Provider } from 'react-redux'
import store from './reducers/store';
import { Provider as AlertProvider } from 'react-alert';
import AlertTemplate from 'react-alert-template-basic';
import { FlagProvider } from 'flagger/react';
import { createTheme, ThemeProvider } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    primary: {
      main: '#7443f4',
      dark: '#5b2fd4',
      light: '#9d7ff8',
      contrastText: '#ffffff',
    },
  },
});

import Login from './views/Login';
import SetupView from './views/Setup';
import DesignerView from './views/TemplateDesignerView';
import ConfigurationView from './views/configuration/ConfigurationView';
import ListView from './views/ListView';
import Search from './views/Search';
import ConsoleView from './views/ConsoleView';
import DocumentTypesView from './views/DocumentTypesView'

import WebsocketConnections from './views/WebsocketConnections';
import PrinterDestinationsView from './views/PrinterDestinationsView';
import FileDropView from './views/FileDropView';

import './assets/css/appv12.css';
import './assets/css/buttons.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import './assets/css/dashboard.css';
import DownloadFile from './views/DownloadFile';
import PollerView from './views/PollerView';

import LicenseCheckWrapper from './components/LicenseCheckWrapper';

import DashboardView from './views/DashboardView';
import UsersView from './views/administration/UsersView';
import UserDetailsView from './views/administration/UserDetailsView';
import ChangePasswordView from './views/administration/ChangePasswordView';
import UserGroupsView from './views/administration/UserGroupsView';
import UserGroupDetailsView from './views/administration/UserGroupDetailsView';
import ImportUsersView from './views/administration/ImportUsersView';

// Normalise double slashes in the pathname (e.g. //site/listview → /site/listview).
// Some X3 configurations generate URLs with a leading double slash, which causes
// React Router's Switch to fail to match any route and fall through to the default
// <Redirect to="/site/dashboard" />.
// MUST run before createBrowserHistory() — the history object snapshots
// window.location at creation time, so the URL must be clean before that call.
try {
    if (window.location.pathname.startsWith('//')) {
        const fixed = window.location.pathname.replace(/^\/+/, '/');
        window.history.replaceState(null, '', fixed + window.location.search + window.location.hash);
    }
} catch (_) {}

const hist = createBrowserHistory();

// TODO: these could be read from a config setting to allow them to be
//altered
const alertoptions = {
  position: 'bottom center',
  timeout: 5000,
  offset: '30px',
  transition: 'scale'
}

// Capture Token from URL synchronously before the async get_flagger call below.
// The X3 parent frame uses history.replaceState on the iframe to strip the Token
// from the URL for security. This fires during the async gap while get_flagger is
// in-flight, so by the time ReactDOM.render runs, window.location.search no longer
// has ?Token=. Saving it to sessionStorage here (synchronous module-level code,
// guaranteed to run before any event-loop yields) lets Auth.js retrieve it as a
// fallback in componentDidMount.
try {
    const _x3t = new URLSearchParams(window.location.search).get('Token');
    if (_x3t) sessionStorage.setItem('_x3Token', _x3t);
} catch (_) {}

// X3 iframe auth: patch global fetch so all /api/ calls automatically include
// the Authorization header when a Token-based session is active.
// In a cross-site iframe Chrome blocks session cookies, so cookie-based auth
// fails for every API call after the initial Token login.  The JWT is stored
// in sessionStorage by getUserFromToken (actions.js) on successful Token auth
// and cleared on logout.  The patch is a no-op for normal (non-iframe) users
// because _x3AuthToken is never written in that path.
(function patchFetchForX3() {
    const _orig = window.fetch.bind(window);
    window.fetch = function(input, init) {
        const x3Token = sessionStorage.getItem('_x3AuthToken');
        if (x3Token) {
            const url = typeof input === 'string' ? input
                      : (input && input.url) ? input.url : '';
            const isApi = url.startsWith('/api/') ||
                          url.startsWith(window.location.origin + '/api/');
            if (isApi) {
                init = Object.assign({}, init);
                init.headers = Object.assign({ 'authorization': x3Token },
                                             init.headers || {});
            }
        }
        return _orig(input, init).then(response => {
            const url = typeof input === 'string' ? input : (input && input.url) ? input.url : '';
            const isLoginCall = url.includes('/api/security/login') || url.includes('/api/security/checkToken');
            // Sage X3 proxy calls forward upstream X3 error statuses and are not
            // indicative of an expired Inscribe session — exclude them so a bad
            // X3 connection doesn't force-redirect a logged-in user to /auth/basic.
            const isX3ProxyCall = url.includes('/api/sageX3/');
            if (response.status === 401 && !isLoginCall && !isX3ProxyCall && !window.location.pathname.startsWith('/auth/')) {
                window.location.assign('/auth/basic');
            }
            return response;
        });
    };
})();

// get flags from flagger before load as flagger.json has to live in src/ to be imported but this causes issues with changing values
fetch(window.location.protocol + '//' + window.location.host + '/api/configuration/get_flagger')
  .then(res => res.json())
  .then(flags => {
    ReactDOM.render(
      <ThemeProvider theme={theme}>
      <FlagProvider flagConfig={flags} subscribeToUpdates>
        <AlertProvider template={AlertTemplate} {...alertoptions}>
          <Provider store={store}>
          <Router history={hist}>
              <Switch>
                <Route
                  path="/site/editor"
                  render={(props) => (
                    <LicenseCheckWrapper>
                      <DesignerView {...props} />
                    </LicenseCheckWrapper>
                  )}
                />
                <Route path="/site/config" component={ConfigurationView}/>
                <Route
                  path="/site/dashboard"
                  render={(props) => (
                    <LicenseCheckWrapper>
                      <DashboardView {...props} />
                    </LicenseCheckWrapper>
                  )}
                />
                <Route
                  path="/site/listview"
                  render={(props) => (
                    <LicenseCheckWrapper>
                      <ListView {...props} />
                    </LicenseCheckWrapper>
                  )}
                />
                <Route
                  path="/site/search"
                  render={(props) => (
                    <LicenseCheckWrapper>
                      <Search {...props} />
                    </LicenseCheckWrapper>
                  )}
                />
                <Route
                  path="/site/console"
                  render={(props) => (
                    <LicenseCheckWrapper>
                      <ConsoleView {...props} />
                    </LicenseCheckWrapper>
                  )}
                />
                <Route
                  path="/site/PrintServer"
                  render={(props) => (
                    <LicenseCheckWrapper>
                      <WebsocketConnections {...props} />
                    </LicenseCheckWrapper>
                  )}
                />
                <Route
                  path="/site/PrintDestinations"
                  render={(props) => (
                    <LicenseCheckWrapper>
                      <PrinterDestinationsView {...props} />
                    </LicenseCheckWrapper>
                  )}
                />
                <Route
                  path="/site/DocumentTypes"
                  render={(props) => (
                    <LicenseCheckWrapper>
                      <DocumentTypesView {...props} />
                    </LicenseCheckWrapper>
                  )}
                />
                <Route
                  path="/site/administration/Users"
                  render={(props) => (
                    <LicenseCheckWrapper>
                      <UsersView {...props} />
                    </LicenseCheckWrapper>
                  )}
                />
                <Route
                  path="/site/administration/UserDetails/:currentUserId?"
                  render={(props) => (
                    <LicenseCheckWrapper>
                      <UserDetailsView {...props} />
                    </LicenseCheckWrapper>
                  )}
                />
                <Route
                  path="/site/administration/ChangePassword/:currentUserId?"
                  render={(props) => (
                    <LicenseCheckWrapper>
                      <ChangePasswordView {...props} />
                    </LicenseCheckWrapper>
                  )}
                />
                <Route
                  path="/site/administration/UserGroups"
                  render={(props) => (
                    <LicenseCheckWrapper>
                      <UserGroupsView {...props} />
                    </LicenseCheckWrapper>
                  )}
                />
                <Route
                  path="/site/administration/UserGroupDetails/:userGroupParameterId?"
                  render={(props) => (
                    <LicenseCheckWrapper>
                      <UserGroupDetailsView {...props} />
                    </LicenseCheckWrapper>
                  )}
                />
                <Route
                  path="/site/administration/ImportUsers"
                  render={(props) => (
                    <LicenseCheckWrapper>
                      <ImportUsersView {...props} />
                    </LicenseCheckWrapper>
                  )}
                />
                <Route
                  path="/site/FileDrop"
                  render={(props) => (
                    <LicenseCheckWrapper>
                      <FileDropView {...props} />
                    </LicenseCheckWrapper>
                  )}
                />
                <Route path="/auth/setup" component={SetupView} />
                <Route path="/auth/basic" component={Login} />
                <Route
                  path="/site/download_file"
                  render={(props) => (
                    <LicenseCheckWrapper>
                      <DownloadFile {...props} />
                    </LicenseCheckWrapper>
                  )}
                />
                <Route
                  path="/site/polling_service"
                  render={(props) => (
                    <LicenseCheckWrapper>
                      <PollerView {...props} />
                    </LicenseCheckWrapper>
                  )}
                />
                <Redirect to="/site/dashboard" />
              </Switch>
            </Router>
          </Provider>
        </AlertProvider>
      </FlagProvider>
      </ThemeProvider>
      , document.getElementById('root'));
  });


registerServiceWorker();